// lurik-frontend/src/presentation/pages/authentication/two-factor/TwoFactorActivationPage.tsx
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTwoFactorService } from '../../../../use-cases/authentication/useTwoFactorService';
import { useAppSelector } from '../../../../application/states/hooks';
import { CodeInput } from './components/CodeInput';
import { BackupCodeInput } from './components/BackupCodeInput';
import { useNavigationService } from '../../../../use-cases/navigation';
import { useUserService } from '../../../../use-cases/user/useUserService';

// ✅ STYLED COMPONENTS (igual ao TwoFactorPage)
const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  padding: 40px;
  width: 100%;
  max-width: 480px;
  text-align: center;
`;

const Title = styled.h1`
  color: #2c3e50;
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  color: #7f8c8d;
  font-size: 16px;
  margin: 0 0 32px 0;
`;

const WarningBox = styled.div`
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
  color: #856404;
`;

export const TwoFactorActivationPage: React.FC = () => {
    const [step, setStep] = useState<'qr' | 'verify'>('qr');
    const [secret, setSecret] = useState('');
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [backupCodes, setBackupCodes] = useState<string[]>([]);
    const [verificationCode, setVerificationCode] = useState('');
    const [loading, setLoading] = useState(false);

    const { activateTwoFactor, verifyTwoFactor } = useTwoFactorService();
    const { navigateTo } = useNavigationService();

    const user = useAppSelector((state) => state.user);

    useEffect(() => {
        handleActivateTwoFactor();
    }, []);

    const handleActivateTwoFactor = async () => {
        try {
            setLoading(true);

            // ✅ SE user.id estiver vazio, buscar do localStorage:
            let userId = user.id;


            if (!userId) {
                userId = localStorage.getItem('TWO_FACTOR_USER_ID') || '';
            }

            if (!userId) {
                console.error('❌ userId não encontrado');
                throw new Error('ID do usuário não encontrado');
            }


            const result = await activateTwoFactor(userId);

            setSecret(result.secret);
            setQrCodeUrl(result.qrCodeUrl);
            setBackupCodes(result.backupCodes);
            setStep('qr');

        } catch (error) {
            console.error('Erro ao ativar 2FA:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async () => {
        try {
            setLoading(true);
            const result = await verifyTwoFactor(verificationCode);

            if (result.success) {
                // ✅ LIMPAR FLAG de ativação obrigatória:
                localStorage.removeItem('REQUIRES_TWO_FACTOR_ACTIVATION');
                localStorage.removeItem('TWO_FACTOR_USER_ID');
                localStorage.removeItem('TEMP_TOKEN');
                
                // ✅ 2FA ativado com sucesso, redirecionar para workspace
                navigateTo({ route: '/workspace' });
              }
        } catch (error) {
            console.error('Erro ao verificar código:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading && step === 'qr') {
        return (
            <Container>
                <Card>
                    <div>Ativando 2FA obrigatório...</div>
                </Card>
            </Container>
        );
    }

    return (
        <Container>
            <Card>
                <Title>Ativação Obrigatória de 2FA</Title>
                <Subtitle>Sua empresa exige 2FA. Configure agora para continuar.</Subtitle>

                <WarningBox>
                    ⚠️ <strong>Obrigatório:</strong> Você deve ativar 2FA para acessar o sistema.
                </WarningBox>

                {step === 'qr' && (
                    <div>
                        <h2>Passo 1: Escaneie o QR Code</h2>
                        <p>Use seu aplicativo authenticator para escanear o QR Code</p>

                        <img src={qrCodeUrl} alt="QR Code 2FA" style={{ maxWidth: '200px' }} />

                        <h3>Chave Secreta</h3>
                        <div style={{
                            fontFamily: 'monospace',
                            background: '#f8f9fa',
                            padding: '12px',
                            borderRadius: '4px',
                            wordBreak: 'break-all',  // ✅ QUEBRAR LINHA EM QUALQUER LUGAR
                            overflowWrap: 'break-word',  // ✅ QUEBRAR PALAVRAS LONGAS
                            whiteSpace: 'pre-wrap'  // ✅ PRESERVAR ESPAÇOS E QUEBRAR LINHA
                        }}>
                            {secret}
                        </div>

                        <h3>Códigos de Backup</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                            {backupCodes.map((code, index) => (
                                <span key={index} style={{ background: 'white', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ffeaa7' }}>
                                    {code}
                                </span>
                            ))}
                        </div>

                        <button onClick={() => setStep('verify')} style={{ marginTop: '16px', padding: '12px 24px', background: '#3498db', color: 'white', border: 'none', borderRadius: '8px' }}>
                            Próximo: Verificar Código
                        </button>
                    </div>
                )}

                {step === 'verify' && (
                    <div>
                        <h2>Passo 2: Verifique seu Código</h2>
                        <p>Digite o código de 6 dígitos gerado pelo seu authenticator</p>

                        <CodeInput
                            value={verificationCode}
                            onChange={setVerificationCode}
                            onVerify={handleVerifyCode}
                            loading={loading}
                        />

                        <button onClick={() => setStep('qr')} style={{ marginTop: '16px', padding: '12px 24px', background: '#f8f9fa', color: '#2c3e50', border: '1px solid #e1e5e9', borderRadius: '8px' }}>
                            Voltar
                        </button>
                    </div>
                )}
            </Card>
        </Container>
    );
};